import React from 'react';
import { MapPin, Users, Building2, RefreshCw } from 'lucide-react';

const GhlStats: React.FC = () => {
  const stats = [
    {
      icon: Users,
      value: '18M+',
      label: 'Licensed Professionals',
      detail: 'Dentists, plumbers, agents, contractors, and more',
      color: 'emerald',
    },
    {
      icon: MapPin,
      value: '50',
      label: 'States Covered',
      detail: 'Every US state and territory in our database',
      color: 'sky',
    },
    {
      icon: Building2,
      value: '50+',
      label: 'Professions',
      detail: 'Real estate, medical, trades, legal, insurance',
      color: 'amber',
    },
    {
      icon: RefreshCw,
      value: 'Daily',
      label: 'Data Updates',
      detail: 'Synced with official state licensing boards',
      color: 'indigo',
    },
  ];

  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    sky: { bg: 'bg-sky-500/10', text: 'text-sky-400', border: 'border-sky-500/20' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
    indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
  };

  return (
    <section className="py-20 bg-slate-950 border-y border-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">The Largest Licensed Professional Database</h2>
          <p className="text-slate-400">Real data from official state licensing boards. Not scraped, not recycled.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat) => {
            const Icon = stat.icon;
            const colors = colorMap[stat.color];
            return (
              <div
                key={stat.label}
                className={`text-center p-6 rounded-2xl bg-slate-900/50 border ${colors.border} hover:border-opacity-50 transition-all duration-300`}
              >
                <div className={`w-12 h-12 mx-auto ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <div className="text-3xl md:text-4xl font-black text-white mb-1">{stat.value}</div>
                <div className="text-sm font-bold text-slate-300 mb-1">{stat.label}</div>
                <div className="text-xs text-slate-500">{stat.detail}</div>
              </div>
            );
          })}
        </div>

        {/* State coverage badges */}
        <div className="mt-12 text-center">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Top Markets by Record Count</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { state: 'FL', records: '5.3M' },
              { state: 'NY', records: '3.7M' },
              { state: 'CT', records: '1.3M' },
              { state: 'CA', records: '936K' },
              { state: 'UT', records: '507K' },
              { state: 'WA', records: '311K' },
              { state: 'VA', records: '219K' },
              { state: 'NV', records: '196K' },
              { state: 'IL', records: '175K' },
              { state: 'TX', records: '113K' },
              { state: 'DE', records: '146K' },
              { state: 'AK', records: '30K' },
              { state: 'CO', records: '21K' },
              { state: 'NJ', records: '5.8K' },
            ].map(({ state, records }) => (
              <span
                key={state}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs"
              >
                <span className="font-bold text-white mr-1.5">{state}</span>
                <span className="text-slate-500">{records}</span>
              </span>
            ))}
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 font-bold">
              +36 more states
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GhlStats;
